/**
 * P1 Issue #15 Fix: React Error Boundary for comprehensive error handling
 */

import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '@/utils/logger';
import { isChunkLoadError, isReloadScheduled } from '@/utils/lazyImports';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Chunk-load errors (typically a stale cached page hitting newly-deployed
    // chunk filenames) are recovered by a one-shot reload in lazyWithRetry. Don't
    // double-reload or report them as crashes; render() shows a neutral loader.
    if (isChunkLoadError(error)) {
      return;
    }

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        context: this.props.context,
      },
    });

    logger.logError(error, {
      errorInfo,
      context: this.props.context,
      componentStack: errorInfo.componentStack
    }, 'ErrorBoundary');

    this.setState({
      error,
      errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      // A chunk-load reload is in flight: show the normal loading state instead
      // of the error UI so the recovery is seamless (no "Something went wrong"
      // flash before the reload completes).
      if (this.state.error && isChunkLoadError(this.state.error) && isReloadScheduled()) {
        return <LoadingSpinner message="Loading page..." />;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            
            <p className="text-gray-600 mb-6">
              {this.props.context 
                ? `An error occurred in ${this.props.context}. Please try again.`
                : 'An unexpected error occurred. Please try again.'
              }
            </p>

            <div className="space-y-3">
              <Button 
                onClick={this.handleRetry}
                className="w-full"
                variant="default"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              
              <Button 
                onClick={this.handleReload}
                variant="outline"
                className="w-full"
              >
                Reload Page
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}